import { useMemo, useState } from "react"
import { useOutletContext } from "react-router-dom"
import Typography from "@mui/material/Typography"
import Stack from "@mui/material/Stack"
import { Box, CircularProgress, useMediaQuery, useTheme } from "@mui/material"
import { DateTime } from "luxon"
import { useTranslation } from "react-i18next"

import type { DeviceDashboardOutletContext } from "types/dashboard.types"
import type { TowerDevicePositionPlotValues } from "types/devicePosition.types"

import {
  getTowerPositionVariant,
  getTowerPositionConfig,
} from "helpers/utils/devicePositionTower"
import { formatPlotTitleTooltip } from "helpers/utils/plots"
import { degreesToString } from "helpers/utils/common"
import { MD_ROW_HEIGHT, SM_ROW_HEIGHT } from "helpers/utils/constants"
import { useDashboardContext } from "helpers/hooks/useDashboardContext"
import useTower from "helpers/hooks/towers/useTower"
import PlotHelpTooltip from "widgets/plots/PlotHelpTooltip"
import PlotDialogIcon from "widgets/plots/PlotDialogIcon"
import { PlotContainer } from "widgets/styled/containers"
import { TowerViewDialog } from "widgets/plots/dialogs/TowerViewDialog"
import SkeletonBox from "widgets/common/SkeletonBox"
import { TowerRelativeAngleView } from "./TowerRelativeAngleView"
import PositionGrid from "./PositionGrid"
import HeadingCompass from "./HeadingCompass"

interface TowerDevicePositionPlotProps {
  values: TowerDevicePositionPlotValues
  isLoading?: boolean
}

const TowerDevicePositionPlot = ({
  values,
  isLoading = false,
}: TowerDevicePositionPlotProps) => {
  const theme = useTheme()
  const { device } = useOutletContext<DeviceDashboardOutletContext>()
  const isSmall = useMediaQuery(theme.breakpoints.only("sm"))
  const { rowHeight: contextRowHeight } = useDashboardContext()
  const rowHeight = contextRowHeight ?? (isSmall ? SM_ROW_HEIGHT : MD_ROW_HEIGHT)
  const { frontTilt, rightTilt, magnitude, heading, _time } = values
  const { t } = useTranslation()
  const [infoPopupOpen, setInfoPopupOpen] = useState(false)

  const { tower, isLoading: isTowerLoading } = useTower({
    id: device.settings?.tower_id,
  })

  const towerHealthEnabled = device.settings?.features?.tower_health?.enabled === true

  const variant = useMemo(
    () => getTowerPositionVariant(device, tower?.type, towerHealthEnabled),
    [device, tower?.type, towerHealthEnabled],
  )

  const orientation = device.settings?.mounting_point?.orientation
  const { viewSrcs, tooltipKey } = getTowerPositionConfig(
    variant,
    towerHealthEnabled,
    orientation,
  )

  const tooltipContent = useMemo(
    () => formatPlotTitleTooltip([tooltipKey], t),
    [tooltipKey, t],
  )

  const cardHeight = `${rowHeight * 2.5}px`
  const magnitudeCardHeight = `${rowHeight * 1.5}px`

  const tiltCardSx = {
    backgroundColor: theme.palette.background.default,
    border: "1px solid",
    borderColor: theme.palette.neutral[400],
    borderRadius: 3,
    overflow: "hidden",
    position: "relative",
  } as const

  const statCardSx = {
    backgroundColor: theme.palette.background.default,
    border: "1px solid",
    borderColor: theme.palette.neutral[400],
    borderRadius: 3,
    overflow: "hidden",
    position: "relative",
  } as const

  const valueSx = { fontSize: "1.8rem", fontWeight: 700 }

  return (
    <>
      <PlotContainer
        type=""
        disableGutters
        maxWidth="xl"
        sx={{ marginTop: "1rem", marginLeft: 0 }}
      >
        {/* Header */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="flex-start"
          justifyContent="space-between"
          mb="0.5rem"
        >
          <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h5">
                {t("plot_titles.LAST_INSTANT_TILTS") + ":"}
              </Typography>
            </Stack>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: "nowrap", fontWeight: 400 }}
            >
              {DateTime.fromMillis(_time).toFormat("yyyy-MM-dd HH:mm:ss")}
            </Typography>
            {(isLoading || isTowerLoading) && <CircularProgress size={20} />}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
            {tooltipContent && <PlotHelpTooltip content={tooltipContent} />}
            {!isTowerLoading && <PlotDialogIcon onClick={() => setInfoPopupOpen(true)} />}
          </Stack>
        </Stack>

        {isTowerLoading ? (
          <SkeletonBox height={rowHeight * 5} />
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "1fr 1fr",
                lg: "2fr 2fr 1fr",
              },
              gridTemplateRows: {
                sm: `${magnitudeCardHeight} ${cardHeight}`,
                lg: `${magnitudeCardHeight} ${cardHeight}`,
              },
              gap: 1,
            }}
          >
            {/* Front tilt — spans both rows at lg */}
            <Box
              sx={{
                ...tiltCardSx,
                gridRow: { lg: "1 / span 2" },
                height: { xs: `${rowHeight * 5}px`, sm: "100%", lg: "100%" },
              }}
            >
              <PositionGrid />
              <TowerRelativeAngleView
                src={viewSrcs.first}
                rowHeight={rowHeight}
                value={frontTilt}
                viewKey="first"
                valueSx={valueSx}
              />
            </Box>

            {/* Right tilt — spans both rows at lg */}
            <Box
              sx={{
                ...tiltCardSx,
                gridRow: { lg: "1 / span 2" },
                height: { xs: `${rowHeight * 5}px`, sm: "100%", lg: "100%" },
              }}
            >
              <PositionGrid />
              <TowerRelativeAngleView
                src={viewSrcs.second}
                rowHeight={rowHeight}
                value={rightTilt}
                viewKey="second"
                valueSx={valueSx}
              />
            </Box>

            {/* Magnitude stat card */}
            <Box
              sx={{
                ...statCardSx,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                px: 2,
                py: 1.5,
                height: { xs: `${rowHeight * 2}px`, sm: "100%", lg: "100%" },
              }}
            >
              <Typography variant="overlineDense" sx={{ mb: 0.5 }}>
                {t("plot_titles.MAGNITUDE")}
              </Typography>
              <Typography
                sx={{
                  fontSize: "1.8rem",
                  fontWeight: 700,
                  color: theme.palette.extras.limeGreen.main,
                  whiteSpace: "nowrap",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {degreesToString(magnitude)}
              </Typography>
            </Box>

            {/* Heading stat card */}
            <Box
              sx={{
                ...statCardSx,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                px: 2,
                py: 1.5,
                height: { xs: `${rowHeight * 2.5}px`, sm: "100%", lg: "100%" },
              }}
            >
              <Typography variant="overlineDense" sx={{ mb: 1 }}>
                {t("plot_titles.HEADING")}
              </Typography>
              <HeadingCompass heading={heading} />
            </Box>
          </Box>
        )}
      </PlotContainer>

      <TowerViewDialog
        open={infoPopupOpen}
        onClose={() => setInfoPopupOpen(false)}
        variant={variant}
      />
    </>
  )
}

export default TowerDevicePositionPlot
